import { inject } from '@angular/core';
import { AGREEMENTS, FOX_API, FOX_TOKEN, PAYMENT_GATEWAYS } from '@kp/env';
import { TiiaAuthService, TiiaService } from '@kp/tiia-services';
import { isObject } from '@kp/utils';
import {
  catchError,
  finalize,
  map,
  of,
  Subject,
  switchMap,
  take,
  throwError,
} from 'rxjs';

import type {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import type { BusinessError } from '@kp/interfaces';

let count = 0;

function addFoxToken(request: HttpRequest<unknown>): HttpRequest<unknown> {
  if (!(request.url.includes(FOX_API) && request.method === 'POST')) {
    return request;
  }

  return request.clone({
    body: isObject(request.body)
      ? { ...request.body, apiKey: FOX_TOKEN }
      : request.body,
  });
}

function addToken(
  request: HttpRequest<unknown>,
  authService: TiiaAuthService,
): HttpRequest<unknown> {
  if (!request.url.includes(__API_URL)) {
    return request;
  }

  const addUserId =
    authService.userId &&
    (request.url.includes(AGREEMENTS) ||
      request.url.includes(PAYMENT_GATEWAYS));

  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${authService.token}`,
      ...(addUserId ? { 'x-user-id': authService.userId } : {}),
    },
    ...(addUserId && !request.url.includes(PAYMENT_GATEWAYS)
      ? { body: addUserIdToReq(request.body, authService) }
      : {}),
  });
}

function addUserIdToReq(model: unknown, authService: TiiaAuthService): unknown {
  if (!isObject(model)) {
    return null;
  }

  const modelRecord = Object.fromEntries(Object.entries(model));

  const additionalAgreementInfo = getAdditionalAgreementInfo(modelRecord);

  if (!additionalAgreementInfo) {
    return modelRecord;
  }

  return {
    ...modelRecord,
    additionalAgreementInfo: {
      ...additionalAgreementInfo,
      'x-user-id': authService.userId,
    },
  };
}

function getAdditionalAgreementInfo(
  model: Record<string, unknown>,
): Record<string, unknown> | null {
  const { additionalAgreementInfo } = model;

  return isObject(additionalAgreementInfo)
    ? Object.fromEntries(Object.entries(additionalAgreementInfo))
    : null;
}

function isBusinessError(code: string, error?: HttpErrorResponse): boolean {
  return (
    error?.error?.businessErrors?.some(
      (businessError: BusinessError) => businessError.code === code,
    ) ?? false
  );
}

// oxlint-disable-next-line max-lines-per-function
const AuthInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(TiiaAuthService);
  const tiiaService = inject(TiiaService);

  if (!request.url.includes(FOX_API)) {
    tiiaService.setLoading$.next(true);
    count += 1;
  }

  const loggedIn$ = authService.token ? of(true) : authService.login$;
  const refreshToken$ = new Subject<void>();
  let refreshTokenInProgress = false;

  return loggedIn$.pipe(
    map(() => addToken(addFoxToken(request), authService)),
    // oxlint-disable-next-line max-lines-per-function
    switchMap(req =>
      next(req).pipe(
        catchError((error: HttpErrorResponse | undefined) => {
          if (error?.status === 401) {
            if (refreshTokenInProgress) {
              if (authService.noSession) {
                setTimeout(() => {
                  refreshToken$.next();
                });
              }

              return refreshToken$.pipe(
                take(1),
                switchMap(() => next(addToken(req, authService))),
              );
            }

            refreshTokenInProgress = true;

            return authService.login(true).pipe(
              switchMap(() => {
                refreshTokenInProgress = false;
                refreshToken$.next();

                return next(addToken(req, authService));
              }),
              catchError((err: HttpErrorResponse | undefined) =>
                authService.processNetworkError(err),
              ),
            );
          }

          /**
           * If it is 'common-007' (Invalid email) error it will ignore
           * an error otherwise it will throw error box and terminate negotiator
           */
          return isBusinessError('common-007', error)
            ? authService.processNetworkError(error)
            : throwError(() => error);
        }),
        finalize(() => {
          if (request.url.includes(FOX_API)) {
            return;
          }

          count -= 1;

          if (!count) {
            tiiaService.setLoading$.next(false);
          }
        }),
      ),
    ),
  );
};

export { AuthInterceptor };
